import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'klarvi_token';

// Helper to get token from localStorage
const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

// Helper to store token
const storeToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch (e) {
    console.error('Error storing token:', e);
  }
};

// Helper for authenticated fetch
const authFetch = async (url, options = {}) => {
  const token = getStoredToken();
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/api/auth/me`);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // Token invalid or expired
        storeToken(null);
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      let errorMessage = 'Error al iniciar sesión';
      if (!response.bodyUsed) {
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {}
      } else {
        if (response.status === 401) errorMessage = 'Credenciales inválidas';
        else if (response.status === 400) errorMessage = 'Datos inválidos';
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Store token in localStorage
    if (data.token) {
      storeToken(data.token);
    }
    
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const register = async (name, email, password, referral_code) => {
    const body = { name, email, password };
    if (referral_code) body.referral_code = referral_code;
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = 'Error al crear cuenta';
      if (!response.bodyUsed) {
        try {
          const error = await response.json();
          errorMessage = error.detail || errorMessage;
        } catch {}
      } else {
        if (response.status === 400) errorMessage = 'El correo ya está registrado';
        else if (response.status === 401) errorMessage = 'Error de autenticación';
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    // Store token in localStorage
    if (data.token) {
      storeToken(data.token);
    }
    
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await authFetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      storeToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Store token after OAuth callback
  const setAuthToken = (token) => {
    storeToken(token);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        register,
        loginWithGoogle,
        logout,
        checkAuth,
        updateUser,
        setAuthToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export authFetch for use in other components
export { authFetch, getStoredToken };
